import React, { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { GraduationCap, Clock, Globe, CheckCircle2, PlayCircle, ArrowLeft, Sparkles, Send, Award, Lock, ListChecks } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { COURSES } from "../data/courses";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reviews } from "../components/Reviews";
import { track } from "../lib/analytics";
import { toast } from "sonner";

const progressKey = (slug) => `taven_progress_${slug}`;
const getProgress = (slug) => { try { return JSON.parse(localStorage.getItem(progressKey(slug))) || {}; } catch { return {}; } };

const TABS = [
  { key: "lessons", label: "Lessons", icon: PlayCircle },
  { key: "tutor", label: "YEDA Coach", icon: Sparkles },
  { key: "quiz", label: "Quiz & Final Test", icon: ListChecks },
  { key: "certificate", label: "Certificate", icon: Award },
];

const lessonContent = (course, lesson) =>
  `In this lesson — "${lesson}" — you'll build practical skills for ${course.title.toLowerCase()}. ` +
  `We cover the core concepts, real examples from the TAVEN ecosystem, and the actions you can take right away. ` +
  `Read through the key points below, then open the AI Tutor to practice with guided questions and a mini-quiz on this exact topic.`;

const buildQuiz = (course) => {
  const objs = course.objectives;
  const other = COURSES.find((c) => c.slug !== course.slug)?.objectives?.[0] || "Guarantee overnight riches";
  return [
    { q: `Which of the following is a real learning objective of "${course.title}"?`,
      options: [objs[0], other, "Promise guaranteed income to customers", "Ignore compliance rules"], answer: 0 },
    { q: `The "${course.title}" course belongs to which TAVEN Academy category?`,
      options: ["Entertainment", course.category, "Cooking", "Sports"], answer: 1 },
    { q: "In the TAVEN ecosystem, affiliates should always share which link to get proper credit?",
      options: ["The general store link", "A competitor's link", "Their personal affiliate/recruit link", "No link"], answer: 2 },
  ];
};

const aiSid = (slug) => `tutor-${slug}-${(localStorage.getItem("taven_ai_sid") || "guest")}`;

export const CoursePage = () => {
  const { slug } = useParams();
  const course = COURSES.find((c) => c.slug === slug);
  const { user, authApi } = useAuth();
  const [tab, setTab] = useState("lessons");
  const [progress, setProgress] = useState(() => getProgress(slug));
  const [activeLesson, setActiveLesson] = useState(null);

  useEffect(() => { if (course) track("course_view", { item_id: course.slug, item_name: course.title }); }, [course]);

  useEffect(() => {
    if (!course || !user) return;
    const all = course.modules.flatMap((m) => m.lessons);
    const done = all.filter((l) => progress[l]).length;
    authApi.post("/customer/progress", {
      course_slug: course.slug, course_title: course.title,
      completed_lessons: done, total_lessons: all.length,
    }).catch(() => {});
  }, [progress, user, course, authApi]);

  if (!course) return <div className="max-w-3xl mx-auto py-24 text-center" data-testid="course-not-found"><h1 className="text-2xl font-display font-black">Course not found</h1><Link to="/education" className="text-taven-gold mt-3 inline-block">Back to Academy</Link></div>;

  const allLessons = course.modules.flatMap((m) => m.lessons);
  const completedCount = allLessons.filter((l) => progress[l]).length;
  const pct = Math.round((completedCount / allLessons.length) * 100);


  const toggleLesson = (l) => {
    const next = { ...progress, [l]: !progress[l] };
    setProgress(next);
    localStorage.setItem(progressKey(slug), JSON.stringify(next));
  };

  return (
    <div data-testid="course-page">
      <PageHero eyebrow={course.category} title={course.title} subtitle={course.summary}>
        <div className="flex flex-wrap gap-2">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 px-3 py-1.5"><Clock className="w-3.5 h-3.5 text-taven-gold" /> {course.duration}</span>
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 px-3 py-1.5"><GraduationCap className="w-3.5 h-3.5 text-taven-gold" /> {course.level}</span>
          {course.languages.map((l) => <span key={l} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 px-3 py-1.5"><Globe className="w-3.5 h-3.5 text-taven-gold" /> {l}</span>)}
        </div>
      </PageHero>

      <section className="py-8 px-4 md:px-8 lg:px-12 mx-auto max-w-6xl">
        <Link to="/education" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-black mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Back to Academy</Link>

        {/* progress bar */}
        <div className="bg-white border border-black/10 p-4 mb-6">
          <div className="flex items-center justify-between text-sm mb-2"><span className="font-bold">Your Progress</span><span className="text-taven-gold font-bold" data-testid="course-progress">{pct}% · {completedCount}/{allLessons.length} lessons</span></div>
          <div className="h-2 bg-taven-gray"><div className="h-2 bg-taven-orange transition-all" style={{ width: `${pct}%` }} /></div>
        </div>

        {/* tabs */}
        <div className="flex flex-wrap gap-2 border-b border-black/10 mb-6">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === t.key ? "border-taven-orange text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "lessons" && (
          <div className="grid lg:grid-cols-[1fr_320px] gap-8" data-testid="lessons-tab">
            <div className="space-y-4">
              {course.modules.map((m, mi) => (
                <div key={m.title} className="border border-black/10 bg-white">
                  <div className="px-5 py-3 bg-taven-gray/50 font-bold flex items-center gap-2"><span className="text-taven-gold">Module {mi + 1}</span> · {m.title}</div>
                  <ul className="divide-y divide-black/5">
                    {m.lessons.map((les) => (
                      <li key={les}>
                        <div role="button" tabIndex={0} onClick={() => setActiveLesson(activeLesson === les ? null : les)} onKeyDown={(e) => { if (e.key === "Enter") setActiveLesson(activeLesson === les ? null : les); }} className="w-full px-5 py-3 flex items-center gap-3 text-sm text-left hover:bg-taven-gray/30 cursor-pointer">
                          <button type="button" onClick={(e) => { e.stopPropagation(); toggleLesson(les); }} className="shrink-0" data-testid={`lesson-complete-${les.slice(0,12)}`}>
                            <CheckCircle2 className={`w-5 h-5 ${progress[les] ? "text-taven-orange fill-taven-orange/20" : "text-black/20"}`} />
                          </button>
                          <span className={progress[les] ? "line-through text-muted-foreground" : ""}>{les}</span>
                        </div>
                        {activeLesson === les && (
                          <div className="px-5 pb-5 pt-1 text-sm text-muted-foreground bg-taven-gray/20">
                            <div className="aspect-video w-full bg-taven-black flex flex-col items-center justify-center gap-1.5 mb-3" data-testid="lesson-video-placeholder">
                              <PlayCircle className="w-10 h-10 text-taven-gold/60" />
                              <p className="text-white/85 text-sm font-bold">Course video coming soon.</p>
                              <p className="text-white/40 text-xs">Real TAVEN Academy videos will appear here.</p>
                            </div>
                            <p className="leading-relaxed">{lessonContent(course, les)}</p>
                            <button onClick={() => setTab("tutor")} className="mt-3 text-xs font-bold uppercase tracking-wider text-taven-gold hover:underline flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Ask YEDA Coach about this</button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <aside className="lg:sticky lg:top-28 h-fit border border-black/10 bg-white p-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] mb-4">What you'll learn</h3>
              <ul className="space-y-3">{course.objectives.map((o) => <li key={o} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-taven-gold shrink-0 mt-0.5" /> {o}</li>)}</ul>
            </aside>
          </div>
        )}

        {tab === "tutor" && <SocratesTutor course={course} />}
        {tab === "quiz" && <Quiz course={course} onPass={() => { track("course_complete", { item_id: course.slug, item_name: course.title }); setProgress((p) => { const n = { ...p, __quizPassed: true }; localStorage.setItem(progressKey(slug), JSON.stringify(n)); return n; }); }} passed={progress.__quizPassed} onGoCert={() => setTab("certificate")} />}
        {tab === "certificate" && <CertificatePurchase course={course} quizPassed={progress.__quizPassed} pct={pct} />}

        <Reviews kind="course" target={course.slug} />
      </section>
    </div>
  );
};

const SocratesTutor = ({ course }) => {
  const [messages, setMessages] = useState([{ role: "assistant", content: `Welcome! I'm your YEDA Coach for "${course.title}". I'll guide you step by step, explain concepts, and help you practice. To start — what do you already know about ${course.title.toLowerCase()}, and what's your main goal for this course?` }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lesson] = useState(course.modules[0]?.lessons[0] || course.title);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim(); if (!msg || loading) return;
    setInput(""); setMessages((m) => [...m, { role: "user", content: msg }]); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: aiSid(course.slug), message: msg, mode: "tutor", course: course.title, lesson });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch { setMessages((m) => [...m, { role: "assistant", content: "The tutor is resting for a moment — please try again." }]); }
    finally { setLoading(false); }
  };

  return (
    <div className="border border-black/10 bg-white flex flex-col h-[60vh]" data-testid="tutor-tab">
      <div className="bg-taven-black text-white p-4 flex items-center gap-3"><Sparkles className="w-5 h-5 text-taven-gold" /><div><p className="font-black font-display leading-none">YEDA Teacher</p><p className="text-[10px] text-taven-gold uppercase tracking-widest mt-1">Your personal learning coach</p></div></div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}><div className={`max-w-[85%] px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-taven-black text-white" : "bg-taven-gray"}`}>{m.content}</div></div>)}
        {loading && <div className="text-sm text-muted-foreground">Tutor is thinking…</div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-black/10 flex gap-2">
        <input data-testid="tutor-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Answer or ask the tutor…" className="flex-1 bg-taven-gray px-3 py-2.5 text-sm outline-none" />
        <button data-testid="tutor-send" disabled={loading} className="bg-taven-orange text-white p-2.5 hover:bg-[#d9561a] disabled:opacity-50"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
};

const Quiz = ({ course, onPass, passed, onGoCert }) => {
  const [quiz] = useState(() => buildQuiz(course));
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = quiz.filter((q, i) => answers[i] === q.answer).length;
  const didPass = score >= 2;

  const submit = () => {
    setSubmitted(true);
    if (didPass) { onPass(); toast.success(`You passed! Score ${score}/${quiz.length}`); }
    else toast.error(`Score ${score}/${quiz.length}. Review the lessons and try again.`);
  };

  return (
    <div className="max-w-2xl" data-testid="quiz-tab">
      <h2 className="text-2xl font-black font-display mb-1">Final Test</h2>
      <p className="text-sm text-muted-foreground mb-6">Pass with at least 2 of 3 correct to unlock your official certificate.</p>
      {passed && <div className="mb-6 p-4 bg-taven-gold/10 border border-taven-gold flex items-center justify-between"><span className="text-sm font-bold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-taven-gold" /> Test passed — certificate unlocked.</span><button onClick={onGoCert} className="text-xs font-bold uppercase tracking-wider text-taven-gold underline">Get Certificate →</button></div>}
      <div className="space-y-6">
        {quiz.map((q, i) => (
          <div key={i} className="border border-black/10 bg-white p-5">
            <p className="font-semibold text-sm mb-3">{i + 1}. {q.q}</p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => (
                <label key={oi} className={`flex items-center gap-2 text-sm px-3 py-2 border cursor-pointer ${submitted && oi === q.answer ? "border-green-500 bg-green-50" : submitted && answers[i] === oi ? "border-red-400 bg-red-50" : "border-black/10 hover:border-taven-gold"}`}>
                  <input type="radio" name={`q${i}`} checked={answers[i] === oi} onChange={() => setAnswers({ ...answers, [i]: oi })} data-testid={`quiz-q${i}-opt${oi}`} /> {opt}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={submit} data-testid="quiz-submit" className="mt-6 bg-taven-black text-white font-bold uppercase tracking-wider py-3.5 px-8 hover:bg-taven-orange transition-colors">Submit Final Test</button>
    </div>
  );
};

const CertificatePurchase = ({ course, quizPassed, pct }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const eligible = quizPassed;

  const buy = async () => {
    if (!name || !email) { toast.error("Please enter your name and email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/certificate/checkout", {
        origin_url: window.location.origin, course_slug: course.slug, course_title: course.title,
        student_name: name, student_email: email,
      });
      track("certificate_purchase", { item_name: course.title, value: 10, currency: "USD" });
      window.location.href = data.url;
    } catch { toast.error("Unable to start checkout. Please try again."); setLoading(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="certificate-tab">
      <div className="border border-black/10 bg-white p-8 text-center">
        <Award className="w-14 h-14 text-taven-gold mx-auto" />
        <h2 className="text-2xl font-black font-display mt-4">Official TAVEN Certificate</h2>
        <p className="text-muted-foreground mt-2">The course is free. Unlock and download your official, verifiable certificate of completion for <strong>${"10.00"}</strong>, issued by TAVEN SYSTEM LLC.</p>

        {!eligible ? (
          <div className="mt-6 p-4 bg-taven-gray/50 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Lock className="w-4 h-4" /> Pass the Final Test to unlock your certificate.</div>
        ) : (
          <div className="mt-6 grid gap-3 text-left">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name (as it appears on certificate)" data-testid="cert-name" className="border border-black/15 px-4 py-3 text-sm outline-none focus:border-taven-gold" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="cert-email" className="border border-black/15 px-4 py-3 text-sm outline-none focus:border-taven-gold" />
            <button onClick={buy} disabled={loading} data-testid="cert-buy" className="bg-taven-orange text-white font-bold uppercase tracking-wider py-4 hover:bg-[#d9561a] transition-colors disabled:opacity-60">{loading ? "Redirecting…" : "Get Official Certificate — $10"}</button>
            <p className="text-xs text-muted-foreground text-center">Secure payment. Your certificate includes a verification ID, your name, course, and completion date.</p>
          </div>
        )}
        <Link to="/education" className="inline-block text-xs font-bold uppercase tracking-wider mt-6 hover:text-taven-gold">Browse more courses →</Link>
      </div>
    </div>
  );
};

export default CoursePage;
